import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import Typography from '@mui/material/Typography';
import { useBudget } from '../../context/BudgetContext';
import { exportExcel } from './exportExcel';

export default function SaveExportBar() {
  const { t, i18n } = useTranslation();
  const { state, derived, resetAndClear } = useBudget();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const handleExcel = async () => {
    setExporting(true);
    try {
      await exportExcel(state, derived, i18n.language || 'es');
      setSnackbar({ open: true, message: t('budgetPlanner.export.excelDone', 'Excel descargado') });
    } catch {
      setSnackbar({ open: true, message: 'Error' });
    } finally {
      setExporting(false);
    }
  };

  const handleReset = () => {
    resetAndClear();
    setConfirmOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          py: 1.5,
          px: 2.5,
          bgcolor: '#F7F9FC',
          borderRadius: 2,
        }}
      >
        <Typography sx={{ fontSize: 13, color: '#5F6B7A', textAlign: 'center', mb: 1.5 }}>
          Tu presupuesto está listo. Puedes exportarlo o seguir ajustando.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleExcel}
            disabled={exporting}
            sx={{
              textTransform: 'none',
              color: '#1E3A5F',
              borderColor: '#1E3A5F',
              '&:hover': { bgcolor: 'rgba(30,58,95,0.04)', borderColor: '#1A3152' },
              fontWeight: 600,
            }}
          >
            {t('budgetPlanner.export.excel', 'Exportar Excel')}
          </Button>

          <Button
            variant="text"
            onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: 'none', color: '#9CA3AF', fontWeight: 500, fontSize: '0.8rem' }}
          >
            {t('budgetPlanner.export.reset', 'Reiniciar todo')}
          </Button>
        </Box>
      </Box>

      {/* Reset confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('budgetPlanner.export.resetTitle', 'Reiniciar presupuesto')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('budgetPlanner.export.resetMessage', 'Esto borrará todos tus datos guardados. ¿Estás seguro?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {t('budgetPlanner.export.cancel', 'Cancelar')}
          </Button>
          <Button onClick={handleReset} color="error" variant="contained">
            {t('budgetPlanner.export.confirm', 'Sí, reiniciar')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity="success" variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
